import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { LayoutDashboard, FileText, UtensilsCrossed, DoorClosed, LogOut, Users, Utensils, Settings, Building2, CalendarRange, Wrench, ChevronDown, HardHat, DollarSign } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV = [
  { name: 'Dashboard', href: '/servicios-generales', icon: LayoutDashboard, end: true },
  { name: 'Contratos', href: '/servicios-generales/contratos', icon: FileText },
  { name: 'Trabajadores', href: '/servicios-generales/trabajadores', icon: Users },
  { name: 'Casino', href: '/servicios-generales/casino', icon: UtensilsCrossed },
  { name: 'Reportes Casino', href: '/servicios-generales/casino-reporte', icon: FileText },
  { name: 'Hotelería', href: '/servicios-generales/habitaciones', icon: DoorClosed },
  { name: 'Línea de tiempo', href: '/servicios-generales/timeline', icon: CalendarRange },
  { name: 'Reglas', href: '/servicios-generales/reglas', icon: Settings },
  { name: 'Mantenedores', icon: Wrench, children: [
    { name: 'Pabellones', href: '/servicios-generales/pabellones', icon: Building2 },
    { name: 'Personal de Soporte', href: '/servicios-generales/personal-soporte', icon: HardHat },
    { name: 'Casino', href: '/servicios-generales/casino-mantenedor', icon: DollarSign },
  ]},
];

export default function ServiciosGeneralesLayout() {
  const { logout, user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [logoUrl, setLogoUrl] = useState(() => localStorage.getItem('cached_logo_url'));
  const [groups, setGroups] = useState(() => {
    // Autoabrir el grupo si la ruta actual pertenece a uno de sus hijos
    const initial = {};
    NAV.forEach(item => {
      if (item.children) {
        initial[item.name] = item.children.some(c => location.pathname.startsWith(c.href));
      }
    });
    return initial;
  });

  useEffect(() => {
    if (logoUrl || !token) return;
    axios.get(`${API}/configuracion`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (r.data?.logo_image) {
          let url = r.data.logo_image;
          if (!url.startsWith('http') && !url.startsWith('data:'))
            url = `${process.env.REACT_APP_BACKEND_URL}${url}`;
          setLogoUrl(url);
          localStorage.setItem('cached_logo_url', url);
        }
      })
      .catch(() => {});
  }, [token, logoUrl]);

  const handleLogout = () => { logout(); navigate('/login'); };
  const iniciales = ((user?.nombre || '?').charAt(0) + (user?.apellido || '').charAt(0)).toUpperCase();

  return (
    <div className="min-h-screen flex bg-gradient-to-br from-emerald-50/40 via-white to-slate-50">
      <aside className="w-64 bg-emerald-800 flex flex-col text-white shadow-xl">
        {/* Logo */}
        <div className="px-5 h-[72px] border-b border-emerald-700 flex items-center justify-center">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Lomas Bayas"
              className="h-12 w-auto object-contain brightness-0 invert block"
              onError={() => { setLogoUrl(null); localStorage.removeItem('cached_logo_url'); }}
              data-testid="sg-logo"
            />
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-11 h-11 rounded-lg bg-white/15 border border-white/30 text-white flex items-center justify-center font-black text-base backdrop-blur">LB</div>
              <span className="font-bold text-white tracking-tight text-lg">Lomas Bayas</span>
            </div>
          )}
        </div>

        {/* Navegación */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.map(item => {
            if (item.children) {
              const open = groups[item.name];
              const hasActive = item.children.some(c => location.pathname.startsWith(c.href));
              return (
                <div key={item.name}>
                  <button
                    onClick={() => setGroups(g => ({ ...g, [item.name]: !g[item.name] }))}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${hasActive ? 'text-white' : 'text-emerald-50'} hover:bg-emerald-600/40`}
                    data-testid={`sg-nav-group-${item.name.toLowerCase()}`}>
                    <item.icon className="w-4 h-4" />
                    <span className="flex-1 text-left">{item.name}</span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
                  </button>
                  {open && (
                    <div className="mt-1 ml-2 pl-3 border-l border-emerald-600/40 space-y-1">
                      {item.children.map(sub => (
                        <NavLink key={sub.href} to={sub.href}
                          className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${isActive ? 'bg-white text-emerald-800 shadow-md' : 'text-emerald-100 hover:bg-emerald-600/40'}`}
                          data-testid={`sg-nav-${sub.href.split('/').pop()}`}>
                          <sub.icon className="w-3.5 h-3.5" />{sub.name}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <NavLink key={item.href} to={item.href} end={item.end}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${isActive ? 'bg-white text-emerald-800 shadow-md' : 'text-emerald-50 hover:bg-emerald-600/40'}`}
                data-testid={`sg-nav-${item.href.split('/').pop()}`}>
                <item.icon className="w-4 h-4" />{item.name}
              </NavLink>
            );
          })}
          <a href="/servicios-generales/casino-kiosko" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-emerald-100 hover:bg-emerald-600/40 border border-dashed border-emerald-400/40 mt-4 transition-colors">
            <Utensils className="w-4 h-4" />Vista Kiosco Casino
          </a>
        </nav>

        {/* Usuario + logout */}
        <div className="p-3 border-t border-emerald-700 bg-emerald-900/20">
          <div className="flex items-center gap-3 mb-2 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-white text-emerald-700 flex items-center justify-center font-bold text-xs shadow">
              {iniciales}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-white truncate">{user?.nombre || 'Usuario'} {user?.apellido || ''}</p>
              <p className="text-[10px] text-emerald-200/80 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-emerald-100 hover:bg-red-500/20 hover:text-red-200 transition-colors font-medium"
            data-testid="sg-logout">
            <LogOut className="w-4 h-4" />Cerrar sesión
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden flex flex-col">
        {/* Header con título del módulo */}
        <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-emerald-100 shadow-[0_1px_0_rgba(6,95,70,0.06)] h-[72px]">
          <div className="px-6 lg:px-8 h-full max-w-7xl mx-auto flex items-end pb-3 justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-xl font-black text-emerald-900 leading-none tracking-tight whitespace-nowrap">
                Servicios Generales <span className="text-emerald-400 font-light mx-1">|</span> <span className="text-emerald-700">Casino &amp; Hotelería</span>
              </h1>
            </div>
          </div>
        </header>

        <div className="p-6 lg:p-8 max-w-7xl mx-auto w-full flex-1">
          <Outlet />
        </div>
        {/* TystaCore footer */}
        <footer className="py-4 px-6 text-center text-sm text-slate-400 border-t border-slate-200">
          © TystaCore {new Date().getFullYear()}
        </footer>
      </main>
    </div>
  );
}
